import type { Editor } from "react-email-editor";
import type { EmailDesign, GeneratedTemplate } from "./AiCampaignTypes";

interface UnlayerHtmlImporter {
  importHtml?: (_html: string) => void;
}

export const loadTemplateIntoEditor = (editor: Editor, template: GeneratedTemplate | null) => {
  if (template?.design) {
    editor.loadDesign(template.design);
    return;
  }

  const importer = (window as unknown as { unlayer?: UnlayerHtmlImporter }).unlayer;
  if (template?.html.trim() && importer?.importHtml) {
    importer.importHtml(template.html);
    return;
  }

  editor.loadBlank({});
};

export const createHtmlBlockDesign = (html: string): EmailDesign =>
  ({
    counters: { u_column: 1, u_row: 1, u_content_html: 1 },
    body: {
      rows: [
        {
          id: "row_1",
          cells: [1],
          columns: [{ id: "column_1", contents: [{ type: "html", values: { html } }], values: {} }],
          values: {},
        },
      ],
      headers: [],
      footers: [],
      values: {},
    },
  } as unknown as EmailDesign);

export const exportEditorHtml = (editor: Editor | null | undefined): Promise<string> =>
  new Promise(resolve => {
    if (!editor) {
      resolve("");
      return;
    }
    editor.exportHtml(data => resolve(data?.html ?? ""));
  });

export const saveEditorDesign = (editor: Editor | null | undefined): Promise<EmailDesign | null> =>
  new Promise(resolve => {
    if (!editor) {
      resolve(null);
      return;
    }
    editor.saveDesign((design: EmailDesign) => resolve(design ?? null));
  });

const RECOMMENDATION_BLOCK_ID_PATTERN = /"(u_(?:row|column|content)_[\w-]*rec_[\w-]*)"/g;
const ID_SUFFIX_LENGTH = 6;
const ID_SUFFIX_RADIX = 36;
const MIN_ROWS_WITH_FOOTER = 2;

interface EditableDesign {
  body?: { rows?: unknown[] };
}

const withFreshBlockIds = <TBlock>(block: TBlock): TBlock => {
  const suffix = Math.random()
    .toString(ID_SUFFIX_RADIX)
    .slice(2, 2 + ID_SUFFIX_LENGTH);
  return JSON.parse(
    JSON.stringify(block).replace(RECOMMENDATION_BLOCK_ID_PATTERN, `"$1_${suffix}"`)
  );
};

export const insertDesignRows = async (
  editor: Editor | null | undefined,
  rows: unknown[]
): Promise<boolean> => {
  const design = (await saveEditorDesign(editor)) as EditableDesign | null;
  const bodyRows = design?.body?.rows;
  if (!editor || !design || !Array.isArray(bodyRows) || !rows.length) {
    return false;
  }

  const hasFooterRow = bodyRows.length >= MIN_ROWS_WITH_FOOTER;
  const insertAt = hasFooterRow ? bodyRows.length - 1 : bodyRows.length;
  bodyRows.splice(insertAt, 0, ...rows.map(withFreshBlockIds));
  editor.loadDesign(design as EmailDesign);
  return true;
};
